// Disappearing messages on the web (M64, D238, docs/chat-round-six.md).
// The server stamps `expiresAt` at delivery and deletes the row in its
// hourly sweep; the device hides a message at that time without waiting.
import {
  DISAPPEARING_SECONDS,
  isExpired,
  type DisappearingDuration,
} from '@twinden/shared';
import { t } from '../i18n';
import { appLocale } from './format';

export const DISAPPEARING_CHOICES: DisappearingDuration[] = [
  'off',
  '24h',
  '7d',
  '90d',
];

/** The choice a chat's setting is, or 'off'. */
export function durationOf(seconds: number | null | undefined) {
  const entry = Object.entries(DISAPPEARING_SECONDS).find(
    ([, value]) => value === seconds
  );
  return (entry?.[0] ?? 'off') as DisappearingDuration;
}

/** "7 days", in the reader's language; a length we don't know is in days. */
export function durationLabel(seconds: number | null | undefined): string {
  const choice = durationOf(seconds);
  if (choice !== 'off') return t(`chat.disappearing.${choice}`);
  if (!seconds) return t('chat.disappearing.off');
  return t('chat.disappearing.7d').replace(
    /\d+/,
    String(Math.round(seconds / 86400))
  );
}

/** The list without messages whose time is up (the same array if none). */
export function withoutExpired<T extends { expiresAt?: string | null }>(
  list: T[],
  now: number = Date.now()
): T[] {
  return list.some((message) => isExpired(message.expiresAt, now))
    ? list.filter((message) => !isExpired(message.expiresAt, now))
    : list;
}

type Expiring = {
  expiresAt?: string | null;
  replyTo?: { expiresAt?: string | null } | null;
};

/**
 * The chat as it should show now: expired messages gone, and quotes of
 * expired messages too (D238). The same array when nothing changed.
 */
export function hideExpired<T extends Expiring>(
  list: T[],
  now: number = Date.now()
): T[] {
  const kept = withoutExpired(list, now);
  if (!kept.some((m) => isExpired(m.replyTo?.expiresAt, now))) return kept;
  return kept.map((m) =>
    isExpired(m.replyTo?.expiresAt, now) ? { ...m, replyTo: null } : m
  );
}

/** When the next message (or quote) in the list disappears, or null. */
export function nextExpiry(
  list: Expiring[],
  now: number = Date.now()
): number | null {
  let next: number | null = null;
  for (const message of list) {
    for (const iso of [message.expiresAt, message.replyTo?.expiresAt]) {
      if (!iso) continue;
      const at = Date.parse(iso);
      if (at > now && (next === null || at < next)) next = at;
    }
  }
  return next;
}

/** "Tue 9:00" or "3 Nov 9:00": when a message disappears. */
export function expiryLabel(iso: string, now: number = Date.now()): string {
  const at = new Date(iso);
  const withinWeek = at.getTime() - now < 6 * 86400_000;
  return at.toLocaleString(appLocale(), {
    ...(withinWeek
      ? { weekday: 'short' as const }
      : { day: 'numeric' as const, month: 'short' as const }),
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** The longest a timer can wait (setTimeout's limit is ~24.8 days). */
export const MAX_TIMER_MS = 2 ** 31 - 1;
